import React from "react";
import {
  Box,
  styled,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableHead,
  useTheme,
  Typography,
  IconButton,
} from "@mui/material";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import { formatValue, formatValueDecimal } from "../../component/Methods";

const BENCHMARK_BODY_ROWS = 5;
const BENCHMARK_ROW_MIN_HEIGHT = 23;

const Tablecell = styled(TableCell)(({ theme }) => ({
  fontSize: "12px",
  padding: "1px 2px 1px 5px",
}));

const PortfolioSummaryTable = ({ 
  titleText, 
  longShortLabels, 
  totals, 
  onToggleGridViewSummary,
}) => {
  const theme = useTheme();
  const summaryContentRowCount = 1 + (totals?.length ?? 0);
  const summaryRowMinHeight = Math.ceil(
    (BENCHMARK_BODY_ROWS * BENCHMARK_ROW_MIN_HEIGHT) / summaryContentRowCount
  );
  const summaryRowSx = {
    height: summaryRowMinHeight,
    "& .MuiTableCell-root": {
      height: summaryRowMinHeight,
      boxSizing: "border-box",
    },
  };

  return (
    <Box
      sx={{
        border: `1px solid ${theme.palette.lightGrey.tableHeader}`,
        borderRadius: "2px",
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: theme.palette.lightGrey.tableHeader,
          px: 1,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ color: theme.palette.lightGrey.textColor }}
        >
          {titleText}
        </Typography>
        <IconButton
          onClick={onToggleGridViewSummary}
          aria-label="Toggle GridView"
          sx={{ height: "20px", width: "20px" }}
        >
          <FullscreenIcon
            sx={{ height: "15px", width: "15px" }}
          />
        </IconButton>
      </Box>
      <Table size="small" sx={{ flex: 1 }}>
        <TableHead>
          <TableRow sx={summaryRowSx}>
            {longShortLabels?.map((i) => {
              return (
                <Tablecell
                  align="center"
                  key={i}
                  sx={{
                    borderRight: `1px solid ${theme.palette.lightGrey.tableHeader}`,
                    color: theme.palette.lightGrey.textColor,
                    fontSize: theme.typography.bodyText12.fontSize,
                    fontWeight: theme.typography.bodyText12.fontWeight,
                  }}
                >
                  {i}
                </Tablecell>
              );
            })}
          </TableRow>
        </TableHead>

        <TableBody>
          {totals.map((row) => (
            <TableRow
              key={row.name}
              sx={{
                ...summaryRowSx,
                "&:hover": {
                  background: theme.palette.grey[200],
                  cursor: "pointer",
                },
              }}
            >
              <Tablecell
                align="left"
                sx={{
                  color: theme.palette.lightGrey.textColor,
                  fontSize: theme.typography.bodyText12.fontSize,
                  fontWeight: theme.typography.bodyText12.fontWeight,
                  borderRight: `1px solid ${theme.palette.lightGrey.tableHeader}`,
                }}
              >
                {row.name}
              </Tablecell>

              <Tablecell
                align="right"
                style={{
                  borderRight: `1px solid ${theme.palette.lightGrey.tableHeader}`,
                  background: theme.palette.grey[100],
                }}
              >
                {row.totalsAUM != null && !isNaN(row.totalsAUM) && isFinite(row.totalsAUM) ? (
                  row.totalsAUM > 0 ? (
                    <>{row.totalsAUM.toFixed(2)}%&nbsp;</>
                  ) : row.totalsAUM < 0 ? (
                    `(${(row.totalsAUM * -1).toFixed(2)}%)`
                  ) : (
                    ""
                  )
                ) : (
                  ""
                )}
              </Tablecell>
              <Tablecell
                align="right"
                style={{
                  borderRight: `1px solid ${theme.palette.lightGrey.tableHeader}`,
                  background: theme.palette.grey[100],
                  color: row.totalsPl != null && !isNaN(row.totalsPl) && isFinite(row.totalsPl) && row.totalsPl > 0 
                    ? (theme.palette.mode === 'dark' ? "#4ade80" : "#16a34a") 
                    : (theme.palette.mode === 'dark' ? "#f87171" : "#dc2626"),
                }}
              >
                {row.totalsPl != null && !isNaN(row.totalsPl) && isFinite(row.totalsPl) ? (
                  row.totalsPl > 0 ? (
                    <>{formatValue(row?.totalsPl, true)}&nbsp;</>
                  ) : row.totalsPl < 0 ? (
                    `(${formatValue(row?.totalsPl * -1, true)})`
                  ) : (
                    ""
                  )
                ) : (
                  ""
                )}
              </Tablecell>
              <Tablecell
                align="right"
                style={{
                  borderRight: `1px solid ${theme.palette.lightGrey.tableHeader}`,
                  background: theme.palette.grey[100],
                  color: row.totalsPcm != null && !isNaN(row.totalsPcm) && isFinite(row.totalsPcm) && row.totalsPcm > 0 
                    ? (theme.palette.mode === 'dark' ? "#4ade80" : "#16a34a") 
                    : (theme.palette.mode === 'dark' ? "#f87171" : "#dc2626"),
                }}
              >
                {row.totalsPcm != null && !isNaN(row.totalsPcm) && isFinite(row.totalsPcm) ? (
                  row.totalsPcm > 0 ? (
                    <>{row.totalsPcm.toFixed(2)}%&nbsp;</>
                  ) : row.totalsPcm < 0 ? (
                    `(${(row.totalsPcm * -1).toFixed(2)}%)`
                  ) : (
                    ""
                  )
                ) : (
                  ""
                )}
              </Tablecell>
              <Tablecell
                align="right"
                style={{
                  borderRight: `1px solid ${theme.palette.lightGrey.tableHeader}`,
                  background: theme.palette.grey[100],
                  color: row?.totalSignals != null && !isNaN(row?.totalSignals) && isFinite(row?.totalSignals) && row?.totalSignals > 0 
                    ? (theme.palette.mode === 'dark' ? "#4ade80" : "#16a34a") 
                    : (theme.palette.mode === 'dark' ? "#f87171" : "#dc2626"),
                }}
              >
                {row.totalSignals != null && !isNaN(row.totalSignals) && isFinite(row.totalSignals) ? (
                  row.totalSignals > 0 ? (
                    <>
                      {formatValueDecimal(row?.totalSignals)}&nbsp;
                    </>
                  ) : row.totalSignals < 0 ? (
                    `(${formatValueDecimal(
                      row?.totalSignals * -1
                    )})`
                  ) : (
                    ""
                  )
                ) : (
                  ""
                )}
              </Tablecell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

export default PortfolioSummaryTable;
